// frontend/src/pages/InviteAccept.jsx
// Accept an invite link — verifies token, shows user details, lets invitee set password.
// Patch: add show/hide toggle to both password fields.
// IP-009 patch: unticked-by-default Master Subscription Agreement
//               checkbox, required before submission is enabled.
// commit: feat(ip009): add Master Subscription Agreement checkbox to invite accept form
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { verifyInvite, acceptInvite } from '../api';

export default function InviteAccept({ onLogin }) {
  const navigate          = useNavigate();
  const [params]          = useSearchParams();
  const token             = params.get('token') || '';

  const [invite,    setInvite]    = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [verifyErr, setVerifyErr] = useState('');

  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPwd,   setShowPwd]   = useState(false);
  const [showConf,  setShowConf]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  useEffect(() => {
    if (!token) {
      setVerifyErr('No invite token found. Check your invite link.');
      setVerifying(false);
      return;
    }
    verifyInvite(token)
      .then(data => { setInvite(data); setVerifying(false); })
      .catch(err  => { setVerifyErr(err.message || 'This invite link is invalid or has expired.'); setVerifying(false); });
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (password !== confirm)      { setError('Passwords do not match.'); return; }
    if (password.length < 12)      { setError('Password must be at least 12 characters.'); return; }
    if (!agreedToTerms)             { setError('You must accept the Master Subscription Agreement to activate your account.'); return; }

    setLoading(true);
    try {
      const { token: jwt, user, org } = await acceptInvite(token, password, agreedToTerms);
      localStorage.setItem('token', jwt);
      onLogin({ ...user, org_id: org.id, org_name: org.name });
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to activate account. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const roleLabel = (r) => (r || '').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div style={{ minHeight: '100vh', background: '#f5f2ed', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 460 }}>

        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#b8860b', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>
            Flow Advisory Group
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#132e50' }}>
            AI Governance Platform
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 10, padding: '32px 36px', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>

          {verifying && (
            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, padding: '20px 0' }}>
              Verifying your invite…
            </div>
          )}

          {!verifying && verifyErr && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#132e50', marginBottom: 8 }}>Invite link invalid</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 24 }}>{verifyErr}</div>
              <Link to="/login" style={{ color: '#132e50', fontSize: 13, fontWeight: 600 }}>Back to sign in</Link>
            </div>
          )}

          {!verifying && invite && (
            <>
              <div style={{ marginBottom: 24, padding: '14px 16px', background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd' }}>
                <div style={{ fontSize: 13, color: '#0369a1', marginBottom: 4 }}>
                  You have been invited to <strong>{invite.orgName}</strong>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {invite.name} · {invite.email} · {roleLabel(invite.role)}
                </div>
              </div>

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>

                {/* Password with show/hide */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>
                    Set your password * <span style={{ color: '#9ca3af', fontWeight: 400 }}>(min 12 characters)</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      placeholder="••••••••••••"
                      style={{ ...inputStyle, paddingRight: 40 }}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(v => !v)}
                      style={eyeBtn}
                      title={showPwd ? 'Hide password' : 'Show password'}
                    >
                      {showPwd ? '🙈' : '👁️'}
                    </button>
                  </div>
                  {password && password.length < 12 && (
                    <div style={inlineErr}>Password must be at least 12 characters.</div>
                  )}
                </div>

                {/* Confirm password with show/hide */}
                <div style={{ marginBottom: 24 }}>
                  <label style={labelStyle}>Confirm password *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConf ? 'text' : 'password'}
                      value={confirm}
                      onChange={e => { setConfirm(e.target.value); setError(''); }}
                      placeholder="••••••••••••"
                      style={{ ...inputStyle, paddingRight: 40, borderColor: confirm && confirm !== password ? '#fca5a5' : '#d1d5db' }}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConf(v => !v)}
                      style={eyeBtn}
                      title={showConf ? 'Hide password' : 'Show password'}
                    >
                      {showConf ? '🙈' : '👁️'}
                    </button>
                  </div>
                  {confirm && confirm !== password && (
                    <div style={inlineErr}>Passwords do not match.</div>
                  )}
                  {confirm && confirm === password && password.length >= 12 && (
                    <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>✓ Passwords match</div>
                  )}
                </div>

                {/* Master Subscription Agreement acceptance, unticked by default (IP-009) */}
                <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <input
                    type="checkbox"
                    id="agreedToTerms"
                    checked={agreedToTerms}
                    onChange={e => { setAgreedToTerms(e.target.checked); setError(''); }}
                    style={{ marginTop: 3 }}
                    required
                  />
                  <label htmlFor="agreedToTerms" style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                    I have read and agree to the{' '}
                    <a href="/legal/terms" target="_blank" rel="noreferrer" style={{ color: '#132e50', fontWeight: 600 }}>
                      Master Subscription Agreement
                    </a>.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading || !agreedToTerms}
                  style={{
                    width: '100%', padding: '12px',
                    background: (loading || !agreedToTerms) ? '#9ca3af' : '#132e50',
                    color: '#fff', border: 'none', borderRadius: 6,
                    fontSize: 14, fontWeight: 700,
                    cursor: (loading || !agreedToTerms) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? 'Activating…' : 'Activate account'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#132e50' };
const inlineErr  = { fontSize: 11, color: '#dc2626', marginTop: 4 };
const eyeBtn     = { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1, color: '#6b7280' };
